import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetch } from "@/src/lib/fetch";
import { Booking, OperatorDetails, OperatorPricing } from "../app/Bookings/model";
import { useToast } from "../components/ui/use-toast";
import { useNavigate } from "react-router-dom";

export const useBookingOperatorMutation = (bookingId: Booking['id'], tab: 'operator' | 'overview' = "overview") => {
    const toast = useToast()
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { mutate: updateOperatorDetails } = useMutation<
        Response,
        unknown,
        {
            operatorDetails: OperatorDetails
        },
        unknown
    >({
        mutationFn: (data) => fetch<any>(`/api/bookings/${bookingId}/operator-details`, "PUT", data).then((res) => res),
        onSuccess: () => {
            toast.toast({
                description: 'Operator details saved',
                variant: "default",
                duration: 1000,
            });
            queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingId] });

            navigate(`/bookings/${bookingId}?tab=${tab}`, { replace: true, });
        },
        onError: (error) => {
            console.log(error);
            toast.toast({
                description: 'Error saving extras',
                variant: "destructive",
                duration: 1000,
            })
        },
    });

    const { mutate: updateOperatorPricing } = useMutation<
        Response,
        unknown,
        {
            operatorPricing: OperatorPricing
        },
        unknown
    >({
        mutationFn: (data) => {
            return fetch<any>(`/api/bookings/${bookingId}/operator-pricing`, "PUT", data).then((res) => res)
        },
        onSuccess: () => {
            toast.toast({
                description: 'Operator pricing saved',
                variant: "default",
                duration: 1000,
            });
            queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingId] });

            navigate(`/bookings/${bookingId}?tab=${tab}`, { replace: true, });
        },
        onError: (error) => {
            console.log(error);
            toast.toast({
                description: 'Error saving pricing',
                variant: "destructive",
                duration: 1000,
            })
        },
    });

    const { mutate: updateOperatorBrokerNotes } = useMutation<
        Response,
        unknown,
        { notes?: string },
        unknown
    >({
        mutationFn: (data) => fetch<any>(`/api/bookings/${bookingId}/operator-broker-notes`, "PATCH", data).then((res) => res),
        onSuccess: () => {
            toast.toast({
                description: 'Operator broker notes saved',
                variant: "default",
                duration: 1000,
            });
            queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingId] });

            navigate(`/bookings/${bookingId}?tab=${tab}`, { replace: true, });
        },
        onError: (error) => {
            console.log(error);
            toast.toast({
                description: 'Error saving operator broker notes',
                variant: "destructive",
                duration: 1000,
            })
        },
    });

    const { mutate: updateOperatorTransfer } = useMutation<
        Response,
        unknown,
        { currencyCode?: string, recipientName?: string, iban?: string, accountNumber?: string },
        unknown
    >({
        mutationFn: (data) => fetch<any>(`/api/bookings/${bookingId}/operator-transfer`, "PUT", data).then((res) => res),
        onSuccess: () => {
            toast.toast({
                description: 'Operator transfer saved',
                variant: "default",
                duration: 1000,
            });
            queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", bookingId] });

            navigate(`/bookings/${bookingId}?tab=${tab}`, { replace: true, });
        },
        onError: (error) => {
            console.log(error);
            toast.toast({
                description: 'Error saving operator transfer',
                variant: "destructive",
                duration: 1000,
            })
        },
    });


    return {
        updateOperatorDetails,
        updateOperatorPricing,
        updateOperatorBrokerNotes,
        updateOperatorTransfer
    }
}